import { useDispatch } from 'react-redux';
import React, { useState, useEffect, useCallback } from 'react';
import { getMessage } from '@tcc/i18n';
import {
  getRegionReport,
  getRegionIsLoading,
  getRegionIsValid
} from '../../../../selectors/regionReportSelectors';
import {
  getModalMetaData,
  getLocale,
  getHarvestArdDataFeatureSelector
} from '../../../../selectors';
import { getCurrentSeasonCropId } from '../../../../selectors/cropSelectors';
import {
  getCurrentSeason,
  getCurrentSeasonCropCode,
} from '../../../../selectors/seasonSelectors';
import { isAirSeederPopulation } from '../../../../selectors/plantingSelectors';
import { REGION_NOTE } from '../../../../constants/modal';
import {
  requestDeleteActivity as deleteActivity,
  setSelectedActivity as setActivity
} from '../../../../actions/scoutingActions';
import { getInversedFieldBoundary } from '../../../../selectors/fieldSelectors';
import { getFieldUuid } from '../../../../selectors/fieldSelectorsNew';
import { trackHeapEvent } from '../../../../actions/analyticsActions';
import {
  closeModal,
  openModal,
  resizeModal,
} from '../../../../actions/modalActions';
import {
  requestRegionReport,
  clearRegionReport,
  requestValidateRegion as validateRegion,
} from '../../../../actions/regionActions';
import ConfirmationModal from '../../../../containers/Modal/ConfirmationModal';
import { RegionReportSpinner } from '../RegionReportSpinner/RegionReportSpinner';
import { RegionReportList } from '../RegionReportList/RegionReportList';
import { RegionReportDetail } from '../RegionReportDetail/RegionReportDetail';
import { RegionReportFooter } from '../RegionReportFooter/RegionReportFooter';
import { RegionThumbnailNoteBox } from '../RegionReportThumbnailNoteBox/RegionThumbnailNoteBox';
import styles from '../RegionReport.css';
import { useAppSelector } from '../../../../utils';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import {
  FeatureFlagKey,
  ModalMode,
  ModalSize,
  YieldRegionReport
} from '../../../../types';
import { currentFieldSelector } from '../../../../selectors/fieldsSelectors';
import { getGeoJson } from '../../../../utils/geo';
import { getHarvestState, getLegacySeasonTag } from '../../../../selectors/harvestSelectors';
import { isValidHarvestData } from '../../../../utils/regionUtils';
import { SILAGE_CORN_CROP_CODE } from '../../../../constants/cropCodes';

const modalStates = {
  list: 'list',
  detail: 'detail',
};

export type RegionReportProps = {
  index: number;
};

export const RegionReport = ({
  index
}: RegionReportProps) => {
  const dispatch = useDispatch();
  const state = useAppSelector(state => state);
  const currentSeasonYear = getCurrentSeason(state, { index });
  const harvestData = getHarvestState(state, { index });
  const hasHarvestData = isValidHarvestData(harvestData);
  const { activity, mode: regionFormMode } = getModalMetaData(state);
  const reportData = getRegionReport(state) ?? null;
  const isLoadingReport = getRegionIsLoading(state);
  const isRegionValid = getRegionIsValid(state);
  const hasAsAppliedApplicationFeature = useFeatureFlag(FeatureFlagKey.WEB_DMB_FRR_AS_APPLIED);
  const fieldName = currentFieldSelector(state).name;
  const fieldBoundary = getInversedFieldBoundary(state);
  const fieldUUID = getFieldUuid(state);
  
  const seasonTag = currentSeasonYear;
  const isHarvestArdDataEnabled = useAppSelector(getHarvestArdDataFeatureSelector);
  const frrSeasonTag = isHarvestArdDataEnabled
    ? getLegacySeasonTag(state, { index })
    : seasonTag;

  const cropCode = getCurrentSeasonCropCode(state, { index });
  const isAirSeeder = isAirSeederPopulation(state, { index }) as boolean;
  const cropId = getCurrentSeasonCropId(state, { index }).toString();
  const locale = getLocale(state);

  const [itemData, setItemData] = useState<YieldRegionReport | null>(null);
  const [deleteConfirmationModal, setDeleteConfirmationModal] = useState(false);
  const [closeConfirmationModal, setCloseConfirmationModal] = useState(false);
  const [child, setChild] = useState(modalStates.list);
  const useSilageCopSupport = cropCode === SILAGE_CORN_CROP_CODE;

  const activityExists = Boolean(activity?.id);

  useEffect(() => {
    const geometry = activity?.location || activity?.geometry;
    dispatch(validateRegion(fieldBoundary, geometry));
  }, [activity?.geometry, fieldBoundary, activity?.location, dispatch]);

  useEffect(() => {
    const geometry = activity?.location || activity?.geometry;
    if (hasHarvestData && isRegionValid && !reportData) {
      dispatch(requestRegionReport({
        fieldUUID,
        seasonTag: frrSeasonTag,
        geometry,
        includesAsAppliedApplicationData: hasAsAppliedApplicationFeature,
      }));
    }
  }, [
    isRegionValid,
    activity?.geometry,
    activity?.location,
    cropId,
    fieldUUID,
    hasHarvestData,
    frrSeasonTag,
    hasAsAppliedApplicationFeature,
    useSilageCopSupport,
    reportData,
    dispatch
  ]);

  const onStep2Click = useCallback(() => {
    setItemData(null);
    setChild(modalStates.list);
  }, []);

  const onItemClick = useCallback((itemDataObj) => {
    setItemData(itemDataObj);
    setChild(modalStates.detail);
  }, []);

  // 
  const onRegionButtonClick = useCallback(() => {
    if (!activityExists) {
      return;
    }

    dispatch(openModal(index, null as never, {
      type: REGION_NOTE,
      size: ModalSize.L,
      mode: ModalMode.EDIT_REGION,
      activity,
    }));
    dispatch(trackHeapEvent('View Field Region Note'));
  }, [index, activityExists, activity, dispatch]);

  const saveClick = useCallback(() => {
    dispatch(openModal(index, null as never, {
      type: REGION_NOTE,
      size: ModalSize.L,
      mode: ModalMode.ADD_REGION,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore It seems we are mixing activities and regions. But we are ignoring it for now give it might be a braking change.
      region: activity?.geometry,
    }));
  }, [index, activity?.geometry, dispatch]);

  const onNotePadClick = useCallback(() => {
    if (regionFormMode === ModalMode.ADD_REGION) {
      saveClick();
    } else {
      onRegionButtonClick();
    }
  }, [regionFormMode, saveClick, onRegionButtonClick]);

  const onDeleteClick = useCallback(() => {
    setDeleteConfirmationModal(false);
    dispatch(deleteActivity(activity, { index }));
    dispatch(clearRegionReport());
  }, [activity, index, dispatch]);

  const onDeleteCancelConfirm = useCallback(() => {
    dispatch(resizeModal(ModalSize.L));
    onDeleteClick();
  }, [dispatch, onDeleteClick]);

  const onCancelConfirm = useCallback(() => {
    dispatch(clearRegionReport());
    dispatch(closeModal(index));
    dispatch(setActivity(index, null));
  }, [dispatch, index]);

  const onCancelClick = useCallback(() => {
    if (regionFormMode === ModalMode.EDIT_REGION) {
      onCancelConfirm();
    } else {
      dispatch(resizeModal(ModalSize.S));
      setCloseConfirmationModal(true);
    }
  }, [onCancelConfirm, regionFormMode, dispatch, setCloseConfirmationModal]);

  const onCancelGoBack = useCallback(() => {
    dispatch(resizeModal(ModalSize.L));
    setCloseConfirmationModal(false);
  }, [dispatch, setCloseConfirmationModal]);

  const onDeleteConfirm = useCallback(() => {
    dispatch(resizeModal(ModalSize.S));
    setDeleteConfirmationModal(true);
  }, [setDeleteConfirmationModal, dispatch]);

  const onDeleteCancelGoBack = useCallback(() => {
    dispatch(resizeModal(ModalSize.L));
    setDeleteConfirmationModal(false);
  }, [dispatch, setDeleteConfirmationModal]);

  const hasRegionReportData =
    reportData && reportData.area && reportData.area.v && isRegionValid;

  const showSaveButton = regionFormMode === ModalMode.ADD_REGION;

  const geoJson = activity?.id ?
    getGeoJson([fieldBoundary, activity.location])
    : getGeoJson([fieldBoundary]);

  if (isLoadingReport) {
    return (
      <div data-test-id='dmb-region-report-loading' className={styles.regionReportContainer}>
        <RegionReportSpinner onCancelClick={onCancelConfirm} />
      </div>
    );
  }

  if (deleteConfirmationModal) {
    return (
      <div
        className={styles.confirmationModalContainer}
        data-test-id='dmb-region-report-delete-confirmation-modal'
      >
        <ConfirmationModal
          confirmButtonText={getMessage('regions.report.modal.delete')}
          handleClickConfirm={onDeleteCancelConfirm}
          handleClickGoBack={onDeleteCancelGoBack}
          handleClickCancel={onCancelConfirm}
          title={getMessage('deleteConfirmationModal.title.report')}
        />
      </div>
    );
  }

  if (closeConfirmationModal) {
    return (
      <div
        className={styles.confirmationModalContainer}
        data-test-id='dmb-region-report-close-confirmation-modal'
      >
        <ConfirmationModal
          confirmButtonText={getMessage('regions.report.modal.close')}
          handleClickConfirm={onCancelConfirm}
          handleClickGoBack={onCancelGoBack}
          handleClickCancel={onCancelConfirm}
          title={getMessage('regions.report.modal.confirm.close')}
          subTitle={getMessage('regions.report.modal.confirm.close.subheading')}
        />
      </div>
    );
  }

  if (!isLoadingReport && !hasRegionReportData) {
    return (
      <div data-test-id='dmb-region-report-no-harvest-data'>
        <h2 className={styles.title}>
          {getMessage('regions.report.modal.title')}
        </h2>
        <div className={styles.noHarvestDataWrapper}>
          {activity && (
            <RegionThumbnailNoteBox
              activity={activity}
              fieldName={fieldName}
              geoJson={geoJson}
              locale={locale}
              onRegionButtonClick={onRegionButtonClick}
              isClickable={activityExists}
              displayNoteButton={activityExists}
            />
          )}
          <p
            className={styles.noHarvestMessage}
            data-test-id='dmb-region-report-no-harvest-data-message'
          >
            {!isRegionValid
              ? getMessage('regions.report.modal.noHarvestDataRegion')
              : getMessage('regions.report.modal.noHarvestData')}
          </p>
        </div>
        <RegionReportFooter
          onCancelClick={onCancelConfirm}
          onDeleteClick={onDeleteConfirm}
          saveClick={saveClick}
          showSaveButton={showSaveButton}
        />

      </div>
    );
  }

  return (
    <div data-test-id='region-report-form' className={styles.regionReportContainer}>
      {activity && reportData && child === modalStates.list && (
        <RegionReportList
          activity={activity}
          cropCode={cropCode}
          data={reportData}
          fieldBoundary={fieldBoundary}
          fieldName={fieldName}
          hasAsAppliedApplications={hasAsAppliedApplicationFeature}
          index={index}
          isAirSeeder={isAirSeeder}
          locale={locale}
          onCancelClick={onCancelClick}
          onClick={onItemClick}
          onDeleteClick={onDeleteConfirm}
          onRegionButtonClick={onNotePadClick}
          saveClick={saveClick}
          showSaveButton={showSaveButton}
        />
      )}
      {itemData && child === modalStates.detail && (
        <RegionReportDetail
          cropCode={cropCode}
          hasAsAppliedApplications={hasAsAppliedApplicationFeature}
          index={index}
          isAirSeeder={isAirSeeder}
          itemData={itemData}
          onCancelClick={onCancelClick}
          onClick={onStep2Click}
          onDeleteClick={onDeleteConfirm}
          saveClick={saveClick}
          showSaveButton={showSaveButton}
        />
      )}
    </div>
  );
};
